/**
 * Which document the open page is, read from its address: the reverse of `CHAIN_PATH` (chainRef.ts),
 * so the sidebar can ask the server "which RFQ holds this one?" and draw that RFQ's trail.
 *
 * Pure and I/O-free. A path that is not one document's own page (a list, `/quotations/new`, a vendor
 * credit note, a printable view) is null: the sidebar then shows the plain menu. Only a real 24-character
 * id counts, so "new" or "register" can never be sent to the server as an id.
 */
import { CHAIN_PATH } from '../traceability/chainRef';
import type { BoxKey } from './lifecycleModel';

export type TrailDocType =
  | 'rfq'
  | 'quotation'
  | 'order'
  | 'purchaseOrder'
  | 'vendorInvoice'
  | 'clientInvoice'
  | 'creditNote';

export interface TrailTarget {
  type: TrailDocType;
  id: string;
}

const OBJECT_ID = /^[a-f\d]{24}$/i;

/** One entry per `CHAIN_PATH` builder except the RFQ, whose id travels in the query string. */
const PREFIXES: ReadonlyArray<readonly [string, TrailDocType]> = [
  [CHAIN_PATH.quotation(''), 'quotation'],
  [CHAIN_PATH.confirmedOrder(''), 'order'],
  [CHAIN_PATH.purchaseOrder(''), 'purchaseOrder'],
  [CHAIN_PATH.vendorInvoice(''), 'vendorInvoice'],
  [CHAIN_PATH.clientInvoice(''), 'clientInvoice'],
  [CHAIN_PATH.creditNote(''), 'creditNote'],
];

export function trailTargetFromLocation(pathname: string, search: string): TrailTarget | null {
  if (pathname === '/rfq') {
    const id = new URLSearchParams(search).get('selected');
    return id && OBJECT_ID.test(id) ? { type: 'rfq', id } : null;
  }
  for (const [prefix, type] of PREFIXES) {
    if (pathname.startsWith(prefix)) {
      const id = pathname.slice(prefix.length).replace(/\/$/, '');
      return OBJECT_ID.test(id) ? { type, id } : null;
    }
  }
  return null;
}

/** The trail box a kind of document sits in. */
export const BOX_OF_TYPE: Record<TrailDocType, BoxKey> = {
  rfq: 'rfq',
  quotation: 'quotation',
  order: 'order',
  purchaseOrder: 'purchaseOrders',
  vendorInvoice: 'vendorInvoices',
  clientInvoice: 'clientInvoices',
  creditNote: 'creditNotes',
};
